import { useOktaAuth } from '@okta/okta-react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ThunkActionDispatch } from 'redux-thunk';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { useDispatch, useSelector } from '../../redux/hooks';
import { signOut } from '../../redux/slices/auth';
import Impersonation from './Impersonation';
import { Partners } from '@mesa-labs/mesa-api/dist/types';

const NavigationContainer = styled.div<{ useLightMode: boolean }>`
  align-items: center;
  backdrop-filter: blur(15px) saturate(125%);
  background: ${(props) => props.theme.colors.Navy};
  box-shadow: 0px 1px 3px rgba(8, 7, 58, 0.03);
  display: flex;
  height: 64px;
  justify-content: space-between;
  left: 0px;
  position: fixed;
  top: 0px;
  transition: background 300ms ease-in-out, backdrop-filter 300ms ease-in-out, box-shadow 300ms ease-in-out;
  width: 100%;
  z-index: 1;

  ${(props) => props.useLightMode && `
    background: ${props.theme.colors.White};
    backdrop-filter: blur(15px) saturate(125%);
    box-shadow: 0px 1px 3px rgba(8, 7, 58, 0.03);
  `}
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0px 32px;
  width: 100%;
`;

const LogoContainer = styled(Link)`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 52px;
`;

const Logo = styled.img`
  height: 20px;
`;

const Left = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
`;

export const BackLink = styled.div`
  align-items: center;
  border-radius: 8px;
  color: #5A05FF;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  justify-content: space-evenly;
  line-height: 20px;
  margin-bottom: 8px;
  max-width: 80px;
  overflow: hidden;
  padding: 8px;
  text-overflow: ellipsis;
  transition: background 100ms ease-in-out;
  white-space: nowrap;

  &:hover {
    background: rgba(129, 156, 251, 0.1);
  }
`;

export const BackLinkIcon = styled.img`
  margin-right: 8px;
`;

const RightIcons = styled.div`
  align-items: center;
  display: flex;
  font-size: 15px;
  justify-content: flex-end;
  gap: 8px;

  & > img {
    margin-right: 24px;

    &:last-of-type {
      margin-right: 0px;
    }
  }
`;

const SignInLink = styled(Link) <{ useLightMode: boolean }>`
  border: 1px solid #122368;
  border-radius: 8px;
  color: ${(props) => props.theme.colors.White};
  cursor: pointer;
  padding: 8px 16px;
  transition: background 300ms ease-in-out;

  &:hover {
    background: #2D265D;
  }

  ${(props) => props.useLightMode && `
    background: #FFF;
    border-radius: 10px;
    border: 1px solid #E1E4EA;
    box-shadow: 0px 1px 2px 0px rgba(10, 13, 20, 0.03);
    color: #525866;
    letter-spacing: -0.084px;
    line-height: 20px;

    &:hover {
      background: #F9FAFB;
    }  
  `};
`;

const NavContainer = styled.div`
  align-content: flex-start;
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: 10px;
`;

const NavLinkContainer = styled(Link) <{ selected: boolean }>`
  align-items: center;  
  border-radius: 6px; 
  display: flex;
  gap: 6px;
  padding: 8px 12px;
  transition: background 300ms ease-in-out;

  ${(props) => props.selected && `
    background: #2D265D;
  `}

  &:hover {
    background: #2D265D;
  }
`;

const NavLinkText = styled.span`
  color: ${(props) => props.theme.colors.White};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  position: relative;
`;

const LogoutText = styled(motion.div)`
  color: ${(props) => props.theme.colors.White};
  font-size: 14px;
  font-weight: 500;
  
  line-height: 20px;
`;

const LogoutContainer = styled(motion.div)`
  align-items: center;
  border-radius: 6px;
  border: 1px solid #122368;
  cursor: pointer;
  display: flex;
  gap: 8px;
  margin-left: auto;
  padding: 4px 8px 4px 4px;
  transition: background 300ms ease-in-out;

  &:hover {
    background: #2D265D;
  }
`;

const LogoutAvatar = styled.div`
  align-items: center;
  background: ${(props) => props.theme.colors.Purple200};
  border: 1px solid #122368;
  border-radius: 4px;
  color: ${(props) => props.theme.colors.Purple950};
  display: flex;
  font-size: 14px;
  font-weight: 500;
  height: 32px;  
  justify-content: center;
  line-height: 20px;
  width: 32px;  
`;

const DashboardIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.3021 16.6667V10.8333M10.6354 16.6667V8.33333M3.96875 16.6667L3.96875 13.3333M11.8076 4.18959L16.1147 5.80472M9.63439 4.50077L4.96894 7.99986M18.186 5.36612C18.6741 5.85427 18.6741 6.64573 18.186 7.13388C17.6978 7.62204 16.9064 7.62204 16.4182 7.13388C15.93 6.64573 15.93 5.85427 16.4182 5.36612C16.9064 4.87796 17.6978 4.87796 18.186 5.36612ZM4.85263 7.86612C5.34079 8.35427 5.34079 9.14573 4.85263 9.63388C4.36448 10.122 3.57302 10.122 3.08487 9.63388C2.59671 9.14573 2.59671 8.35427 3.08487 7.86612C3.57302 7.37796 4.36448 7.37796 4.85263 7.86612ZM11.5193 2.86612C12.0075 3.35427 12.0075 4.14573 11.5193 4.63388C11.0311 5.12204 10.2397 5.12204 9.75153 4.63388C9.26338 4.14573 9.26338 3.35427 9.75153 2.86612C10.2397 2.37796 11.0311 2.37796 11.5193 2.86612Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClientsIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.88607 9.16667H4.4694C4.00269 9.16667 3.76934 9.16667 3.59108 9.25749C3.43427 9.33739 3.30679 9.46487 3.2269 9.62167C3.13607 9.79993 3.13607 10.0333 3.13607 10.5V17.5M14.3861 9.16667H16.8027C17.2694 9.16667 17.5028 9.16667 17.6811 9.25749C17.8379 9.33739 17.9653 9.46487 18.0452 9.62167C18.1361 9.79993 18.1361 10.0333 18.1361 10.5V17.5M14.3861 17.5V5.16667C14.3861 4.23325 14.3861 3.76654 14.2044 3.41002C14.0446 3.09641 13.7897 2.84144 13.4761 2.68166C13.1195 2.5 12.6528 2.5 11.7194 2.5H9.55273C8.61931 2.5 8.1526 2.5 7.79608 2.68166C7.48248 2.84144 7.22751 3.09641 7.06772 3.41002C6.88607 3.76654 6.88607 4.23325 6.88607 5.16667V17.5M18.9694 17.5H2.30273M9.80273 5.83333H11.4694M9.80273 9.16667H11.4694M9.80273 12.5H11.4694" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SuppliersIcon = () => (
  <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_101_8844)">
      <path d="M17.1357 15.75V14.25C17.1357 12.8521 16.1797 11.6775 14.8857 11.3445M12.2607 2.46807C13.3602 2.91311 14.1357 3.99098 14.1357 5.25C14.1357 6.50902 13.3602 7.58689 12.2607 8.03193M13.3857 15.75C13.3857 14.3522 13.3857 13.6533 13.1574 13.1019C12.8529 12.3669 12.2689 11.7828 11.5338 11.4784C10.9825 11.25 10.2836 11.25 8.88574 11.25H6.63574C5.23792 11.25 4.539 11.25 3.98769 11.4784C3.25261 11.7828 2.66859 12.3669 2.3641 13.1019C2.13574 13.6533 2.13574 14.3522 2.13574 15.75M10.7607 5.25C10.7607 6.90685 9.4176 8.25 7.76074 8.25C6.10389 8.25 4.76074 6.90685 4.76074 5.25C4.76074 3.59315 6.10389 2.25 7.76074 2.25C9.4176 2.25 10.7607 3.59315 10.7607 5.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_101_8844">
        <rect width="18" height="18" fill="white" transform="translate(0.635742)" />
      </clipPath>
    </defs>
  </svg>
);

const LIGHT_MODE_ROUTES = new Set([
  '/sign-in',
  '/sign-in-ops',
]);

function Navigation(): React.ReactElement {
  const location = useLocation();
  const dispatch = useDispatch() as ThunkActionDispatch<any>;
  const partnerId = useSelector((state) => state.auth.partnerId);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isAuthorized = useSelector((state) => state.auth.isAuthorized);
  const email = useSelector((state) => state.auth.email);

  const { oktaAuth } = useOktaAuth();

  const handleSignOut = () => {
    dispatch(signOut(oktaAuth));
  };

  const useLightMode = LIGHT_MODE_ROUTES.has(location.pathname);

  return (
    <NavigationContainer useLightMode={useLightMode}>
      <HeaderContainer>
        <Left>
          <LogoContainer to="/">
            <Logo src={useLightMode ? '/assets/mesa-purple.svg' : '/assets/mesa.svg'} />
          </LogoContainer>
        </Left>

        {isAuthorized && (
          <NavContainer>
            {process.env.NODE_ENV !== 'production' && partnerId !== Partners.JLL && (
              <NavLinkContainer to="/" selected={location.pathname === '/'}>
                <DashboardIcon />
                <NavLinkText>Dashboard</NavLinkText>
              </NavLinkContainer>
            )}

            <NavLinkContainer to="/suppliers" selected={location.pathname.startsWith('/suppliers')}>
              <SuppliersIcon />
              <NavLinkText>Suppliers</NavLinkText>
            </NavLinkContainer>

            <NavLinkContainer to="/clients" selected={location.pathname.startsWith('/clients')}>
              <ClientsIcon />
              <NavLinkText>Clients</NavLinkText>
            </NavLinkContainer>
          </NavContainer>
        )}

        <RightIcons>
          {isLoggedIn && (
            <Impersonation />
          )}

          {isAuthorized && (
            <LogoutContainer onClick={handleSignOut}>
              <LogoutAvatar>{email?.slice(0, 1)[0]?.toUpperCase() || 'U'}</LogoutAvatar>
              <LogoutText>
                Log Out
              </LogoutText>
            </LogoutContainer>
          )}
          {!isAuthorized && (
            <SignInLink to="/sign-in" useLightMode={useLightMode}>Sign in</SignInLink>
          )}
        </RightIcons>
      </HeaderContainer>
    </NavigationContainer>
  );
}

export default Navigation;
